import React from 'react';
import Immutable from 'immutable';
import { stateMap } from './state';
import { addListener, removeListener } from './index';
import invariant from '../../invariant';
// Native shallow equality implementation (replaces is-equal-shallow dependency)
const shallowEqual = (obj1, obj2) => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (let key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
};

export function connect(_options) {
  return function (Component) {
    let options = _options;
    if (_options.interests === undefined) {
      options = { interests: _options };
    }
    return wrapperConstructor(Component, options);
  };
}

let wrapperConstructor = function (
  Component,
  { interests, load, loader, propTypes = {} }
) {
  let _interests = interests;
  interests =
    typeof _interests === 'function'
      ? _interests
      : (props) => {
          return _interests;
        };
  load = !!load ? load : () => {};
  let Loader = loader;

  invariant(
    typeof Component.propTypes === 'object',
    'You must specify propTypes for all wrapped components. Check: %s',
    Component.displayName
  );

  let wrapperPropTypes = { ...Component.propTypes, ...propTypes };
  // Prune propTypes derived from state
  for (let prop of Object.values(interests({}))) {
    delete wrapperPropTypes[prop];
  }

  return class Wrapper extends React.Component {
    static propTypes = wrapperPropTypes;
    static displayName = `${Component.displayName}-Connector`;

    constructor(props) {
      super(props);
      this.interests = interests(props);
      this.state = this.stateFromMap(stateMap(this.interests));
      this._definedShouldRender =
        Object.values(interests(props)).indexOf('shouldRender') !== -1;
    }

    stateFromMap(stateMap) {
      let stateMutation = {};
      for (let key in stateMap) {
        stateMutation[this.interests[key]] = stateMap[key];
      }
      return stateMutation;
    }

    UNSAFE_componentWillMount() {
      this.registerInterest(this.props);
      if (!this._definedShouldRender || !!this.state.shouldRender === false) {
        load(this.props, this.state);
      }
    }

    registerInterest(props) {
      this.listenerId = addListener(this.interests, (stateMap) => {
        this.setState(this.stateFromMap(stateMap));
      });
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
      let nextInterests = interests(nextProps);
      if (!shallowEqual(this.interests, nextInterests)) {
        this.interests = nextInterests;
        removeListener(this.listenerId);
        this.registerInterest(nextProps);
        load(nextProps, this.state);
        this.setState(this.stateFromMap(stateMap(interests(nextProps))));
      }
    }

    componentWillUnmount() {
      removeListener(this.listenerId);
    }

    render() {
      let { shouldRender, ...propsFromState } = this.state;
      let props = { ...propsFromState, ...this.props };

      for (let prop in propTypes) {
        if (Object.keys(Component.propTypes).indexOf(prop) === -1) {
          delete props[prop];
        }
      }

      if (this._definedShouldRender && shouldRender !== true) {
        if (Loader !== undefined) {
          return <Loader />;
        } else {
          return null;
        }
      } else {
        return (
          <Component
            ref={(component) => {
              this.componentRef = component;
            }}
            {...props}
          />
        );
      }
    }
  };
};
